interface RateLimitEntry {
  attempts: number;
  lastAttempt: number;
  cooldownUntil?: number;
}

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  cooldownMs: number;
}

class RateLimiter {
  private static instance: RateLimiter;
  private storage = new Map<string, RateLimitEntry>();

  // Default configurations for different actions
  private configs: Record<string, RateLimitConfig> = {
    signin: {
      maxAttempts: 5,
      windowMs: 15 * 60 * 1000, // 15 minutes
      cooldownMs: 5 * 60 * 1000  // 5 minutes cooldown after max attempts
    },
    signup: {
      maxAttempts: 3,
      windowMs: 10 * 60 * 1000, // 10 minutes
      cooldownMs: 10 * 60 * 1000 // 10 minutes cooldown
    },
    resendConfirmation: {
      maxAttempts: 3,
      windowMs: 5 * 60 * 1000,  // 5 minutes
      cooldownMs: 2 * 60 * 1000  // 2 minutes cooldown
    },
    passwordReset: {
      maxAttempts: 3,
      windowMs: 15 * 60 * 1000, // 15 minutes
      cooldownMs: 5 * 60 * 1000  // 5 minutes cooldown
    }
  };

  static getInstance(): RateLimiter {
    if (!RateLimiter.instance) {
      RateLimiter.instance = new RateLimiter();
    }
    return RateLimiter.instance;
  }

  /**
   * Check if an action is allowed for a given key (usually email + action type)
   */
  isAllowed(key: string, action: string): { allowed: boolean; remainingCooldown?: number; error?: string } {
    const config = this.configs[action];
    if (!config) {
      console.warn(`No rate limit configuration found for action: ${action}`);
      return { allowed: true };
    }

    const now = Date.now();
    const entry = this.storage.get(key);

    // If no entry exists, this is the first attempt
    if (!entry) {
      return { allowed: true };
    }

    // Check if we're still in cooldown period
    if (entry.cooldownUntil && now < entry.cooldownUntil) {
      const remainingCooldown = Math.ceil((entry.cooldownUntil - now) / 1000);
      return {
        allowed: false,
        remainingCooldown,
        error: `Too many attempts. Please wait ${Math.ceil(remainingCooldown / 60)} minutes before trying again.`
      };
    }

    // Check if the time window has expired (reset attempts)
    if (now - entry.lastAttempt > config.windowMs) {
      // Window expired, reset attempts
      this.storage.delete(key);
      return { allowed: true };
    }

    // Check if we've exceeded max attempts within the window
    if (entry.attempts >= config.maxAttempts) {
      const cooldownUntil = now + config.cooldownMs;
      this.storage.set(key, {
        ...entry,
        cooldownUntil
      });
      
      const remainingCooldown = Math.ceil(config.cooldownMs / 1000);
      return {
        allowed: false,
        remainingCooldown,
        error: `Too many attempts. Please wait ${Math.ceil(remainingCooldown / 60)} minutes before trying again.`
      };
    }

    return { allowed: true };
  }

  /**
   * Record an attempt for the given key and action
   */
  recordAttempt(key: string, action: string, success: boolean = false): void {
    const config = this.configs[action];
    if (!config) {
      return;
    }

    const now = Date.now();
    const existing = this.storage.get(key);

    if (success) {
      // Successful attempt - reset the counter
      this.storage.delete(key);
      return;
    }

    if (!existing) {
      // First failed attempt
      this.storage.set(key, {
        attempts: 1,
        lastAttempt: now
      });
    } else {
      // Additional failed attempt
      const attempts = now - existing.lastAttempt > config.windowMs ? 1 : existing.attempts + 1;
      
      this.storage.set(key, {
        attempts,
        lastAttempt: now,
        cooldownUntil: existing.cooldownUntil
      });
    }
  }

  /**
   * Get remaining cooldown time for a key
   */
  getRemainingCooldown(key: string): number {
    const entry = this.storage.get(key);
    if (!entry?.cooldownUntil) return 0;

    const remaining = Math.max(0, entry.cooldownUntil - Date.now());
    return Math.ceil(remaining / 1000);
  }

  /**
   * Clear all rate limit data (useful for testing or admin actions)
   */
  clear(): void {
    this.storage.clear();
  }

  /**
   * Clear rate limit data for a specific key
   */
  clearKey(key: string): void {
    this.storage.delete(key);
  }

  /**
   * Update rate limit configuration for an action
   */
  updateConfig(action: string, config: Partial<RateLimitConfig>): void {
    this.configs[action] = { ...this.configs[action], ...config };
  }
}

export default RateLimiter.getInstance();
export type { RateLimitConfig };
